import { useToast } from "@/components/ui/toastercustom";
import { ERROR_MESSAGES_LOGIN } from "@/constants/messages";
import {
  IAuthGetCode,
  IRegisterData,
} from "@/features/authentication/interfaces/IAuth";
import {
  getCodeOTP,
  register,
} from "@/features/authentication/services/auth.services";
import useProfileStore from "@/stores/profile";
import { onSetCookie } from "@/utils/handleCookie";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { ChangeEvent, useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

const useRegister = () => {
  const { toast } = useToast();
  // Router
  const router = useRouter();

  // Store
  const { setToken, setProfile, setRefreshToken } = useProfileStore(
    useShallow((states) => ({
      setToken: states.setToken,
      setProfile: states.setProfile,
      setRefreshToken: states.setRefreshToken,
    })),
  );

  // State
  const [isDisabledGetCode, setIsDisabledGetCode] = useState<boolean>(false);
  const [isDisabledSubmit, setIsDisabledSubmit] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  // USE MUTATION ---------------------------------------------------------
  const { mutateAsync: mutateGetCode } = useMutation({
    mutationKey: ["getOTPCode"],
    mutationFn: async (data: Pick<IAuthGetCode, "email">) => getCodeOTP(data),
    onError: (error) => {
      if (typeof error.message == "string") {
        toast({
          title: "Error",
          type: "error",
          text: error.message,
        });
      }
    },
    onSettled: () => {
      setIsDisabledGetCode(false);
    },
    onSuccess: (response) => {
      setIsDisabledGetCode(false);
      if (response.status) {
        toast({
          title: "Success",
          type: "success",
          text: "Get code success",
        });
      } else {
        toast({
          title: "Error",
          type: "error",
          text: response.message || "Failed to get code",
        });
      }
    },
  });

  /**
   * @description This hook handle register form
   */
  const { mutateAsync } = useMutation({
    mutationKey: ["register"],
    mutationFn: async (
      data: Pick<
        IRegisterData,
        "confirmPassword" | "email" | "password" | "otp_number"
      >,
    ) =>
      register({
        email: data.email,
        password: data.password,
        otp_number: data.otp_number,
      }),
    onSuccess: (response) => {
      setIsDisabledSubmit(false);
      if (response.status) {
        // After successful login, display toast
        toast({
          title: "Success",
          type: "success",
          text: response.message || "Login successful",
        });

        // Save data to local storage using Zustand
        // TODO: BE should return app data user
        setProfile(response.data);
        setToken(response.token);
        onSetCookie({
          token: response.token,
          refresh_token: response.refresh_token.token,
          created_from: response.data?.created_from,
        });
        setRefreshToken(response.refresh_token);
        router.push("/my-agents");
      } else {
        // When status is false
        toast({
          title: "Error",
          type: "error",
          text: response.message || ERROR_MESSAGES_LOGIN,
        });
      }
    },
    onError: (error) => {
      setIsDisabledSubmit(false);
      if (typeof error.message === "string") {
        toast({
          title: "Error",
          type: "error",
          text: error.message || ERROR_MESSAGES_LOGIN,
        });
      }
    },
  });

  // -----------------------------------------------------------------------

  // FUNCTION -------------------------------------------------------------

  /**
   * @description Handle get code and send to email
   */
  const onGetCode = useCallback(
    (email: string, event: ChangeEvent<unknown>) => {
      event.preventDefault();
      setIsDisabledGetCode(true);
      //NOTE: Must be able to apply with or without information.
      // Check if profile is not null
      // if (!profile) {
      //   // console.error('Profile is null');
      //   return;
      // }

      mutateGetCode({
        email: email,
        // NOTE: api backend can register without recaptcha
        // TODO: Update recaptcha using recaptcha library
        // recaptcha: "recaptcha",
      });
    },
    [mutateGetCode],
  );

  /**
   * @description Handle submit form
   */
  const onSubmit = (
    data: Pick<
      IRegisterData,
      "confirmPassword" | "email" | "password" | "otp_number"
    >,
  ) => {
    setIsDisabledSubmit(true);
    mutateAsync(data);
  };

  // Return the function
  return {
    onGetCode,
    onSubmit,
    isDisabledGetCode,
    isDisabledSubmit,
    showPassword,
    showConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
  };
};

export default useRegister;
